import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import moment from 'moment';

import { ReduxState } from '../../reducers';
import { DueDiligenceChartDTO } from '../../actions/types/dueDiligence';
import { updateDueDiligenceChart, processDueDiligenceChart } from '../../actions/dueDiligence';
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE, DateLogs } from '../../actions/types/portfolioDesigner';
import { DatePicker } from '../../components/common/styled/DatePicker';
import { LooseObject } from '../../common/types';
import { TimeRangePicker } from '../common/TimeRangePicker';
import { TimeRange, TimeRangeDates } from '../common/ranges';
import { WithWindowDimensionsProps, withWindowDimensions } from '../common/withWindowDimensions';
import Chart from '../../components/common/Chart';
import { ResultDateLogs } from '../PortfolioDesigner/ResultDateLogs';
import { AccountLogo } from '../common/styled/AccountLogo';
import { LogoHorizontalImage } from '../../assets';
import Text from '../common/Text';

interface ReduxStateProps extends WithWindowDimensionsProps{
    item?: DueDiligenceChartDTO
    dueDiligenceChartOptions: LooseObject
    processingChart: boolean
    date_logs?: DateLogs
    hasLogo: boolean
}

interface ReduxActionProps {
    updateDueDiligenceChart: typeof updateDueDiligenceChart;
    processDueDiligenceChart: typeof processDueDiligenceChart;
}

type ComponentProps = ReduxActionProps & ReduxStateProps

interface ComponentState {
    start_date?: Date;
    end_date?: Date;
}

class InputTab extends React.Component<ComponentProps, ComponentState> {
    state: ComponentState = {
        start_date: DEFAULT_MIN_DATE,
        end_date: DEFAULT_MAX_DATE,
    }

    componentDidMount(): void {
        this.updateDateRangeFromItem(this.props.item);
    }

    updateDateRangeFromItem = (item?: DueDiligenceChartDTO) => {
        if (!item) {
            this.setState({ start_date: DEFAULT_MIN_DATE, end_date: DEFAULT_MAX_DATE })

            return;
        }

        const {start_date, end_date} = item;

        this.setState({ start_date: moment(start_date ?? DEFAULT_MIN_DATE).toDate(), end_date: moment(end_date ?? DEFAULT_MAX_DATE).toDate()})
    }

    componentDidUpdate(prevProps: Readonly<ComponentProps>): void {
        if (prevProps.item?.start_date !== this.props.item?.start_date || prevProps.item?.end_date !== this.props.item?.end_date || prevProps.item?.date_range !== this.props.item?.date_range) {
            this.updateDateRangeFromItem(this.props.item);
        }
    }

    onStartDateUpdated = (date: Date) => {
        const {item} = this.props;
        this.props.updateDueDiligenceChart(item?.id ?? 0, { start_date: moment(date).format('YYYY-MM-DD'), date_range: null })
    }

    onEndDateUpdated = (date: Date) => {
        const {item} = this.props;
        this.props.updateDueDiligenceChart(item?.id ?? 0, { end_date: moment(date).format('YYYY-MM-DD'), date_range: null })
    }

    onRangePicker = (range: TimeRange, { start_date, end_date }: TimeRangeDates) => {
        const {item} = this.props
        const {date_range} = item ?? {};

        if (range.unit === date_range?.unit && range.duration === date_range?.duration) {
            this.props.updateDueDiligenceChart(item?.id ?? 0, { date_range: null })

            return;
        }
        
        this.props.updateDueDiligenceChart(item?.id ?? 0, { start_date: moment(start_date).format('YYYY-MM-DD'), end_date: moment(end_date).format('YYYY-MM-DD'), date_range: range })
    }

    render() {
        const {dueDiligenceChartOptions, item, hasLogo} = this.props;
        const {start_date, end_date} = this.state;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', }}>
                <Box sx={{ display: 'flex', marginBottom: '1rem', flexDirection: { xs: 'column', lg: 'row' }, height: '100%'  }}>
                    <TimeRangePicker onRangePicker={this.onRangePicker} max={DEFAULT_MAX_DATE} value={item?.date_range ?? undefined} />
                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'row',
                        marginTop: { xs: '1rem', lg: 'unset' },
                        marginLeft: { xs: 'unset', lg: '2.5rem' },
                    }}>
                        <DatePicker
                            sx={{ marginRight: '1rem'}}
                            date={start_date ?? DEFAULT_MIN_DATE}
                            format={'MM/dd/yyyy'}
                            minDate={DEFAULT_MIN_DATE}
                            maxDate={DEFAULT_MAX_DATE}
                            onDateChanged={this.onStartDateUpdated} 
                        />
                        <DatePicker
                            date={end_date ?? DEFAULT_MAX_DATE}
                            format={'MM/dd/yyyy'}
                            minDate={DEFAULT_MIN_DATE}
                            maxDate={DEFAULT_MAX_DATE}
                            onDateChanged={this.onEndDateUpdated} 
                        />
                    </Box>
                </Box>
                <Box sx={{ height: '100%' }}>
                    <Box sx={{ height: '100%' }}>

                        <Box>
                            <Chart 
                                id="dueDiligenceCharts" 
                                title={item?.name ?? ''}
                                sx={{ 
                                    width: { xs: this.props.dimensions.width - 100, lg: this.props.dimensions.width - 625} ,
                                    height: '75rem',
                                    backgroundColor: '#0000',
                                    zIndex: 0,
                                    position: 'relative',
                                    border: (theme) => `.10rem ${theme.palette.primary.main} solid`,
                                }}
                                loading={this.props.processingChart}
                                chartOptions={dueDiligenceChartOptions}
                                showExport={true}
                                footer={
                                    <ResultDateLogs
                                        logs={this.props.date_logs}
                                    />
                                }
                                attribution={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '1rem' }}>
                                        <Box>
                                            {hasLogo && <AccountLogo sx={{ maxHeight: '4rem', marginTop: '.25rem'}} />} 
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Text type='text_small' sx={{ marginRight: '1rem' }}>{moment().tz('America/New_York').format('MM-DD-YYYY hh:mm:ss a')} ET</Text>
                                            <Text type='text_small' sx={{ marginRight: '1rem' }}>Powerd By: </Text>
                                            <LogoHorizontalImage sx={{ height: '3rem', maxWidth: '40rem'}} />
                                        </Box>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        )
    }
}


const mapStateToProps = ({ dueDiligence, clientManagement }: ReduxState) => {
    const { logoAvailable: hasLogo } = clientManagement
    const {selectedChartItem, dueDiligenceChartOptions, processingChart, result } = dueDiligence;

    return { item: selectedChartItem, dueDiligenceChartOptions, processingChart, date_logs: result?.date_logs, hasLogo }
}

export default connect(mapStateToProps, { updateDueDiligenceChart, processDueDiligenceChart })(withWindowDimensions(InputTab) as any)